import { z } from "zod";

/**
 * `S1-a` COMPORTEMENT DU COMPTE (`P1-ITL-079`): **`GET /api/v1/bank/files/{id}/behaviour`'s answer, one contract for the door and for every client** (A48: a stub speaks the provider's contract).
 * The field names are the design record's (`D2-behaviour.md` §4 and §9: `statement_lines`, `window`, `income.by_month`, `balance.eve_of_salary`, `recurring.loanlike`, `focus`, items 12 to 15, 20, 22), in snake_case
 * as the record writes them; every figure is served and the browser computes nothing; every fact cites the ids of the statement lines it was read from.
 *
 * **One definition, two readings** (`DECISION-ANSWER-LENIENT`'s pattern): the shapes are written once in {@link buildSchemas} with the object constructor as its one parameter; `z.strictObject` is the
 * contract (stubs, the OpenAPI parity spec: an extra key is a mismatch), `z.looseObject` is the READ twin a client parses the live answer with (extra keys tolerated at every level, every required field and type
 * identical). The door does not validate at runtime: a read door must never turn an unforeseen field into an error page.
 */
function buildSchemas(object: typeof z.strictObject) {
  const amount = z.number();
  const lineId = z.string();

  const statementLineSchema = object({
    id: lineId,
    statement_id: z.string(),
    statement_month: z.string(),
    page: z.number().int().nullable(),
    date: z.string(),
    label: z.string(),
    /** Credit minus debit, by code. */
    amount,
    balance: amount.nullable(),
  });

  const windowSchema = object({
    from: z.string().nullable(),
    to: z.string().nullable(),
    months_read: z.array(z.string()),
    months_unread: z.array(z.string()),
  });

  const incomeMonthSchema = object({
    month: z.string(),
    salary_amount: amount.nullable(),
    salary_day: z.number().int().nullable(),
    salary_line_id: lineId.nullable(),
    other_credits: z.array(object({ line_id: lineId, amount })).nullable(),
  });

  const eveSchema = object({
    month: z.string(),
    date: z.string(),
    amount,
    line_id: lineId,
    share_of_salary: object({ percent: z.number().int() }),
  });

  const loanlikeSchema = object({
    id: z.string(),
    label: z.string(),
    amount,
    day: z.number().int().nullable(),
    months_seen: z.number().int(),
    declared: z.literal(false),
    line_ids: z.array(lineId),
  });

  const behaviourSchema = object({
    state: z.enum(["read", "not_yet_read"]),
    reading_id: z.string().nullable(),
    window: windowSchema,
    statement_lines: z.array(statementLineSchema),
    income: object({
      by_month: z.array(incomeMonthSchema),
      total_by_month: z.array(object({ month: z.string(), total_credits: amount })),
      salary_regularity: object({
        day: z.number().int().nullable(),
        months_seen: z.number().int(),
        months_read: z.number().int(),
      }).nullable(),
      monthly_mean: object({ amount, months_counted: z.number().int() }).nullable(),
      range: object({ min: amount, max: amount }).nullable(),
    }),
    balance: object({
      daily: z.array(object({ date: z.string(), amount, line_id: lineId.nullable() })),
      eve_of_salary: z.array(eveSchema),
      lowest_eve: object({
        month: z.string(),
        date: z.string(),
        amount,
        line_id: lineId,
        others: z.array(object({ month: z.string(), amount })),
      }).nullable(),
    }),
    recurring: object({ loanlike: z.array(loanlikeSchema) }),
    focus: object({
      kind: z.enum(["undeclared_recurring", "low_point"]),
      fact_id: z.string(),
      words: z.string().nullable(),
    }).nullable(),
    salary_withheld: z.string().nullable(),
  });

  const bankBehaviourAnswerSchema = object({ behaviour: behaviourSchema });

  return { statementLineSchema, behaviourSchema, bankBehaviourAnswerSchema };
}

const strict = buildSchemas(z.strictObject);
const loose = buildSchemas(z.looseObject);

export const behaviourStatementLineSchema = strict.statementLineSchema;
export const behaviourSchema = strict.behaviourSchema;
export const bankBehaviourAnswerSchema = strict.bankBehaviourAnswerSchema;

/** The READ twin: what a client parses the live answer with; extra keys pass at every level, a missing required field or a wrong type fails as in the strict schema. */
export const bankBehaviourAnswerReadSchema = loose.bankBehaviourAnswerSchema;

export type BankBehaviourAnswer = z.infer<typeof bankBehaviourAnswerSchema>;
export type Behaviour = z.infer<typeof behaviourSchema>;
